// @ts-nocheck
import React, { useState } from 'react';
import { Box } from '@mui/material';
// @ts-ignore GridColDef is not exported from @mui/x-data-grid
import { GridColDef } from '@mui/x-data-grid';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { StyledDataGrid } from './styles';
import { useNavigate } from 'react-router-dom';
import { isRowClickable } from '../../utils/verificationAdmin';

interface CustomDataGridProps {
    rows: Array<Record<string, any>>;
    columns: GridColDef[];
    pageSize?: number;
    loading?: boolean;
    getRowId?: (row: Record<string, any>) => string | number;
    page?: number;
    onPageChange?: (page: number) => void;
    isAdmin?: boolean;
}

interface CustomPaginationProps {
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
    page,
    totalPages,
    onPageChange
}) => {
    const getVisiblePages = (): (number | string)[] => {
        if (totalPages <= 1) return [];

        const pages: (number | string)[] = [];

        // Always show first page
        pages.push(1);

        // If current page is more than 3 from start, show ellipsis
        if (page > 3) {
            pages.push('...');
        }

        // Show page before current (if not already shown)
        if (page > 2 && page - 1 !== 1) {
            pages.push(page - 1);
        }

        // Show current page (if not first or last)
        if (page !== 1 && page !== totalPages) {
            pages.push(page);
        }

        // Show page after current (if not already going to be last)
        if (page < totalPages - 1 && page + 1 !== totalPages) {
            pages.push(page + 1);
        }

        // If current page is more than 3 from end, show ellipsis
        if (page < totalPages - 2) {
            pages.push('...');
        }

        // Always show last page (if not already shown)
        if (totalPages > 1) {
            pages.push(totalPages);
        }

        return pages;
    };

    if (totalPages <= 1) return null;

    return (
        <Box className="custom-pagination">
            <button
                className="pagination-nav-button"
                onClick={() => onPageChange(page - 1)}
                disabled={page === 1}
            >
                <IoChevronBack size={16} />
                Previous
            </button>

            {getVisiblePages().map((pageNum, index) => (
                <button
                    key={index}
                    className={`page-circle ${pageNum === page ? 'active' : ''} ${typeof pageNum !== 'number' ? 'ellipsis' : ''}`}
                    onClick={() => typeof pageNum === 'number' && onPageChange(pageNum)}
                    disabled={typeof pageNum !== 'number'}
                >
                    {typeof pageNum === 'number' ? pageNum : '...'}
                </button>
            ))}

            <button
                className="pagination-nav-button"
                onClick={() => onPageChange(page + 1)}
                disabled={page === totalPages}
            >
                Next
                <IoChevronForward size={16} />
            </button>
        </Box>
    );
};

const CustomStyledDataGrid: React.FC<CustomDataGridProps> = ({
    rows,
    columns,
    pageSize = 10,
    loading = false,
    getRowId,
    page: externalPage,
    onPageChange: externalOnPageChange,
    isAdmin = false,
}) => {
    const [internalPage, setInternalPage] = useState(1);
    const navigate = useNavigate();

    // Use external page state if provided, otherwise use internal state
    const currentPage = externalPage || internalPage;
    const handlePageChange = externalOnPageChange || setInternalPage;

    // Calculate pagination based on ALL rows (after filtering in parent component)
    const totalPages = Math.ceil(rows.length / pageSize);

    // Ensure current page is within bounds to prevent errors
    const safePage = Math.min(Math.max(1, currentPage), Math.max(1, totalPages));

    const startIndex = (safePage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedRows = rows.slice(startIndex, endIndex);

    const FooterComponent = () => (
        <CustomPagination
            page={safePage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
        />
    );
    return (
        <Box className="styled-datagrid-container"
            sx={{
                height: 'calc(100vh - 200px)', // Set a fixed height
                overflow: 'auto', // Enable scrolling
                paddingRight: '12px', // Buffer for scroll bar
                '& .MuiDataGrid-root': {
                    height: '100%'
                }
            }}>
            <StyledDataGrid
                rows={paginatedRows}
                columns={columns}
                loading={loading}
                getRowId={getRowId}
                hideFooter={false}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                disableDensitySelector
                slots={{
                    footer: FooterComponent
                }}
                getRowClassName={(params) => {
                    return isRowClickable(params.row.status, isAdmin) ? 'clickable' : 'non-clickable';
                }}
                onRowClick={(params) => {
                    if (!isRowClickable(params.row.status, isAdmin)) return;
                    navigate(`/verifications/${params.row.id}`);
                }}
            />
        </Box>
    );
};

export default CustomStyledDataGrid;